import { forwardRef, useMemo, useState } from 'react';
import type { Ref } from 'react';
import type { TextInput as RNTextInput } from 'react-native';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { getTokenValue, isIos, Token, useComposedRefs, useProps } from '@tamagui/core';

import { PasswordHide, PasswordShow } from '../../../__generated__/icons';
import { AnalyticsProps } from '../../../helpers/analytics';
import { getLineHeightTokens } from '../../../helpers/fontTokens';
import { isKeyOfObject } from '../../../helpers/utils';
import { Box, BoxProps, PressableBox } from '../../Box';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { Text, TextProps } from '../../Text';
import { useFrameFocus, useInputId, useInputName, useInputState } from './hooks';
import { InputError } from './InputError';
import { InputFrame } from './InputFrame';
import { InputLabel } from './InputLabel';
import type { InputProps, InputStateProp } from './InputProps';
import { TextInputField } from './TextInputField';
import type { TextInputFieldProps } from './TextInputField';

const { textInput: textInputTokens } = tokens;

const PrefixSuffixText = ({
  size,
  state,
  children,
  ...props
}: React.PropsWithChildren<
  Required<Pick<TextInputFieldProps, 'size'>> &
    InputStateProp &
    Omit<TextProps, 'tag' | 'typography' | 'color'>
>) => {
  return (
    <Text
      tag="span"
      typography={textInputTokens.typography.valueText[size]}
      color={
        isKeyOfObject(state, textInputTokens.color.content.prefixSuffix.state)
          ? textInputTokens.color.content.prefixSuffix.state[state]
          : textInputTokens.color.content.prefixSuffix.default
      }
      {...props}
    >
      {children}
    </Text>
  );
};

export type WithTitle = {
  /**
   * An optional string to show the title
   */
  title: string;
  /**
   * Must be `never` when `title` is present
   */
  ariaLabel?: never;
  /**
   * Must be `never` when `title` is present
   */
  ariaLabelledBy?: never;
};

export type WithAriaLabel = {
  /**
   * An optional string to label the input for a11y
   */
  ariaLabel: string;
  /**
   * Must be `never` when `ariaLabel` is present
   */
  title?: never;
  /**
   * Must be `never` when `ariaLabel` is present
   */
  ariaLabelledBy?: never;
};

export type WithAriaLabelledBy = {
  /**
   * An optional string to reference the element with the label for input
   */
  ariaLabelledBy: string;
  /**
   * Must be `never` when `ariaLabelledBy` is present
   */
  title?: never;
  /**
   * Must be `never` when `ariaLabelledBy` is present
   */
  ariaLabel?: never;
};

export type TextInputBaseProps = Partial<AnalyticsProps> &
  Omit<InputProps, 'title'> &
  Pick<
    TextInputFieldProps,
    | 'multiline'
    | 'onFocus'
    | 'onBlur'
    | 'onChangeText'
    | 'onKeyPress'
    | 'autoCapitalize'
    | 'autoComplete'
    | 'autoCorrect'
    | 'autoFocus'
    | 'value'
    | 'placeholder'
    | 'keyboardType'
    | 'inputMode'
    | 'maxLength'
    | 'returnKeyType'
    | 'enterKeyHint'
    | 'secureTextEntry'
    | 'clearTextOnFocus'
    | 'enablesReturnKeyAutomatically'
    | 'passwordRules'
    | 'spellCheck'
    | 'textContentType'
    | 'importantForAutofill'
    | 'onSelectionChange'
    | 'testID'
  > &
  (WithTitle | WithAriaLabel | WithAriaLabelledBy) & {
    /**
     * An optional string to visually prepend to the input
     */
    prefix?: string;
    /**
     * An optional string to visually append to the input
     */
    suffix?: string;
    /**
     * Suggested text for the input value
     */
    suggestion?: string;
    /**
     * Callback for when the suggestion is clicked
     */
    onSuggestion?: () => void;
    /**
     * Optional props to pass to the frame component (e.g., to tweak padding)
     */
    frameProps?: BoxProps;
    /**
     * Optional children to render inside the frame on the right side (useful
     * for toggles, loading spinners, etc.)
     */
    children?: React.ReactNode;
    /**
     * Optional property to pre-fill the value of the input - value should not update
     */
    prefilledValue?: string;
  };

/**
 * TextInputBase is the base component used for input components like TextInput and TextArea
 */
export const TextInputBase = forwardRef(function TextInputBase(
  {
    title,
    supportText,
    id,
    name,
    value,
    prefilledValue,
    placeholder,
    errorText,
    invalid,
    ariaLabel,
    ariaLabelledBy,
    disabled,
    required,
    prefix,
    suffix,
    multiline,
    onFocus,
    onBlur,
    onChangeText,
    analyticsName,
    trackImpression,
    textContentType,
    suggestion,
    onSuggestion,
    frameProps,
    children,
    ...propsIn
  }: TextInputBaseProps,
  ref: Ref<RNTextInput | HTMLInputElement>,
) {
  const { size = 'medium', ...props } = useProps(propsIn);

  const [showPassword, setShowPassword] = useState(textContentType === 'password');

  const inputId = useInputId(id);

  const elementRef = useInputName(name);
  const combinedRefs = useComposedRefs(ref, elementRef);

  const { inputRef, handleFramePress } = useFrameFocus(combinedRefs);

  const { state, handleFocus, handleBlur } = useInputState({
    disabled,
    errorText,
    invalid,
    onBlur,
    onFocus,
  });

  const heightProps = useMemo(() => getInputHeightProps({ multiline, size }), [multiline, size]);

  const [internalValue, setInternalValue] = useState(value ?? prefilledValue ?? '');
  const handleTextChange = (text: string) => {
    setInternalValue(text);
    onChangeText?.(text);
  };

  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      gap={textInputTokens.padding.gap.textAndField[size]}
      eventName="inputViewEvent"
    >
      {!!title && (
        <InputLabel
          size={size}
          state={state}
          title={title}
          supportText={supportText}
          id={inputId}
          required={required}
        />
      )}
      <InputFrame
        size={size}
        state={state}
        onPress={handleFramePress}
        {...frameProps}
        flex={multiline ? 0 : 1}
        overflow={multiline ? 'hidden' : undefined}
      >
        <Box
          flex={1}
          flexDirection="row"
          alignItems={multiline ? 'flex-start' : 'center'}
          gap={textInputTokens.padding.gap.prefixValueSuffix.default}
        >
          {!!prefix && typeof prefix === 'string' && (
            <PrefixSuffixText size={size} state={state}>
              {prefix}
            </PrefixSuffixText>
          )}
          <Box flexShrink={1} flexGrow={1} overflow={multiline ? 'hidden' : undefined}>
            <TextInputField
              secureTextEntry={showPassword}
              size={size}
              color={
                isKeyOfObject(state, textInputTokens.color.content.valueText.state)
                  ? textInputTokens.color.content.valueText.state[state]
                  : textInputTokens.color.content.valueText.default
              }
              id={inputId}
              value={value}
              defaultValue={prefilledValue}
              placeholder={placeholder}
              disabled={disabled}
              editable={!disabled}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChangeText={handleTextChange}
              ref={inputRef}
              aria-invalid={!!errorText || invalid}
              aria-required={required}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              multiline={multiline}
              textContentType={textContentType}
              scrollEnabled={multiline}
              {...heightProps}
              {...props}
            />
          </Box>
          {!!suffix && typeof suffix === 'string' && (
            <>
              {/* invisible suffix aligned to the right because the text field has flexGrow={1} */}
              <Box opacity={0}>
                <PrefixSuffixText size={size} state={state} aria-hidden={true}>
                  {suffix}
                </PrefixSuffixText>
              </Box>
              <Box
                flexDirection="row"
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                alignItems="center"
                gap={textInputTokens.padding.gap.prefixValueSuffix.default}
                aria-hidden={true}
              >
                {/* invisible prefix that floats over the text field and displays to the left of the user input */}
                {!!prefix && typeof prefix === 'string' && (
                  <PrefixSuffixText size={size} state={state} aria-hidden={true}>
                    {prefix}
                  </PrefixSuffixText>
                )}
                {/* invisible text that floats over the text field and is the same width as the user input */}
                <Text
                  tag="span"
                  typography={textInputTokens.typography.valueText[size]}
                  numberOfLines={1}
                  flexShrink={1}
                  opacity={0}
                  aria-hidden={true}
                >
                  {value ?? prefilledValue ?? internalValue}
                </Text>
                {/* visible suffix that floats over the text field and displays immediately to the right of the user input */}
                <PrefixSuffixText size={size} state={state} aria-hidden={false}>
                  {suffix}
                </PrefixSuffixText>
              </Box>
            </>
          )}
        </Box>

        {textContentType === 'password' && (
          <PressableBox
            aria-label={'Toggle password visibility'}
            testID="show-password-toggle"
            analyticsName="show-password-toggle"
            onPress={() => {
              setShowPassword(!showPassword);
            }}
          >
            {showPassword ? <PasswordShow /> : <PasswordHide />}
          </PressableBox>
        )}

        {children}
      </InputFrame>

      <InputError errorText={errorText} />

      {!suggestion ? null : (
        <PressableBox
          aria-label=""
          analyticsName="input-suggestion"
          onPress={onSuggestion}
          testID="input-suggestion"
          justifyContent="flex-start"
        >
          <Text
            tag="span"
            mt="$4x"
            color="$contentAccentBrand"
            cursor="pointer"
            typography={textInputTokens.typography.supportText.default}
          >
            Did you mean "{suggestion}"?
          </Text>
        </PressableBox>
      )}
    </TrackableContainer>
  );
});

const getInputHeightProps = ({
  multiline,
  size,
}: Pick<TextInputBaseProps, 'multiline' | 'size'>) => {
  const props: Pick<
    TextInputFieldProps,
    'numberOfLines' | 'height' | 'lineHeight' | 'textAlignVertical' | 'paddingVertical'
  > = {};

  const lineHeightToken = textInputTokens.typography.valueText[size];

  // For some reason providing a lineHeight to single-line inputs on iOS cuts off
  // descenders like "g" and "y", so for now let's only provide it for Android/web
  // and use height on iOS instead. Possibly fixed in a later version of React Native
  // by this PR: https://github.com/facebook/react-native/pull/38359
  if (isIos && !multiline) {
    const lineHeightTokenVariable = getLineHeightTokens()?.[lineHeightToken];
    props.height =
      lineHeightTokenVariable && typeof lineHeightTokenVariable !== 'number'
        ? lineHeightTokenVariable?.val
        : undefined;
  } else {
    props.lineHeight = lineHeightToken;
  }

  if (multiline) {
    const numberOfLines = getTokenValue(
      tokens.textInput.text.row.multiline.default as Token,
      'text',
    );

    props.numberOfLines = numberOfLines;
    props.textAlignVertical = 'top';
    props.lineHeight = lineHeightToken;
  }

  return props;
};
